import React, { ReactNode, useState } from "react";
import { Steps, Form, Button, Alert, FormInstance } from "antd";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  configureSiteMutation,
  createSiteMutation,
  updateSiteMutation,
} from "../../services/client/@tanstack/react-query.gen";
import {
  Location,
  SiteReadable as Site,
  UpdateSiteConfigurationDto,
} from "../../services/client";
import { StepOne } from "./Components/StepOne";
import { StepTwo } from "./Components/StepTwo";
import { StepThree } from "./Components/StepThree";
import { StepFour } from "./Components/StepFour";
import { useIsViewer } from "../../hooks/useIsViewer";
import { DisableForViewer } from "../common/DisableForViewer";

const { Step } = Steps;

type StepDescription<T = unknown> = {
  title: string;
  content: (form: FormInstance<T>) => ReactNode;
  onSubmit: (values: T) => void;
  initialValues: T | undefined;
};

const useStepManager = (steps: StepDescription[]) => {
  const [current, setCurrent] = useState(0);
  const next = () => setCurrent((prev) => Math.min(prev + 1, steps.length - 1));
  const prev = () => setCurrent((prev) => Math.max(prev - 1, 0));
  return { current, next, prev, isLastStep: current === steps.length - 1 };
};

const SiteStepper: React.FC = () => {
  const [site, setSite] = useState<Site>();
  const [globalError, setGlobalError] = useState<string>();
  const [globalLoading, setGlobalLoading] = useState(false);

  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isViewer = useIsViewer();

  const createSite = useMutation({
    ...createSiteMutation(),
    onSuccess: (data) => {
      setSite(data);
      setGlobalError(undefined);
      setGlobalLoading(false);
      next();
    },
    onError: (error: Error) =>
      setGlobalError(error.message || "Error creating site"),
  });

  const updateSite = useMutation({
    ...updateSiteMutation(),
    onSuccess: (data) => {
      setSite(data);
      setGlobalError(undefined);
      setGlobalLoading(false);
      next();
    },
    onError: (error: Error) =>
      setGlobalError(error.message || "Error updating site"),
  });

  const configureSite = useMutation({
    ...configureSiteMutation(),
    onSuccess: (data) => {
      setSite(data);
      setGlobalError(undefined);
      setGlobalLoading(false);
      next();
    },
    onError: (error: Error) =>
      setGlobalError(error.message || "Error adding site configuration"),
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const steps: StepDescription<any>[] = [
    {
      title: "Step 1",
      content: (form: FormInstance) => <StepOne form={form} />,
      onSubmit: (values: {
        name: string;
        site_id: string;
        coordinates: string;
        country: string;
      }) => {
        const { name, site_id, coordinates, country } = values;
        const parsedCoordinates = coordinates.split(",").map(Number);
        const location: Location = {
          name,
          coordinates: [parsedCoordinates[0], parsedCoordinates[1], 0],
        };

        if (site && site.site_id === site_id) {
          updateSite.mutate({
            path: { site_id: site.site_id },
            body: { name, location, site_id, country },
          });
        } else {
          if (site && site.site_id !== site_id) {
            setSite(undefined);
          }
          createSite.mutate({
            body: { name, location, site_id, country },
          });
        }
      },
      initialValues: site,
    },
    {
      title: "Step 2",
      content: () => <StepTwo site={site} />,
      onSubmit: () => {
        if (!site) {
          setGlobalError("Site not found.");
        }
        next();
      },
      initialValues: site,
    },
    {
      title: "Step 3",
      content: () => <StepThree site={site} setSite={setSite} />,
      onSubmit: () => {
        if (!site || !site.site_id) {
          return navigate("/404");
        }
        updateSite.mutate(
          {
            // @ts-expect-error this fails because of a mismatch between derived properties and the uploaded site configuration
            // We are ignoring for now for speed of execution
            body: site,
            path: { site_id: site.site_id },
          },
          { onSuccess: () => next() },
        );
      },
      initialValues: site,
    },
    {
      title: "Step 4",
      content: (form: FormInstance) => <StepFour site={site} form={form} />,
      onSubmit: (
        configurations: Omit<UpdateSiteConfigurationDto, "site_configuration">,
      ) => {
        if (!site || !site.site_id) {
          return navigate("/404");
        }

        configureSite.mutate(
          {
            path: { site_id: site?.site_id },
            body: {
              optimization_configuration:
                configurations.optimization_configuration,
            },
          },
          { onSuccess: () => navigate(`/sites/${site.site_id}`) },
        );
      },
      initialValues: site?.configurations ?? undefined,
    },
  ];

  const { current, next, prev, isLastStep } = useStepManager(steps);

  const handleSubmit = (values: unknown) => {
    setGlobalError(undefined);
    steps[current].onSubmit(values);
  };

  return (
    <div className="max-w-4xl mt-8 mx-auto p-8 bg-white rounded shadow-xl">
      <Steps current={current} className="my-8">
        {steps.map((step, index) => (
          <Step key={index} title={step.title} />
        ))}
      </Steps>

      <div className="steps-content">
        <Form
          form={form}
          disabled={isViewer}
          onFinish={handleSubmit}
          initialValues={steps[current].initialValues}
          layout="vertical"
        >
          {steps[current].content(form)}

          <div className="steps-action mt-8">
            {current > 0 && (
              <Button onClick={prev} style={{ marginRight: 8 }}>
                Previous
              </Button>
            )}
            <DisableForViewer>
              <Button
                type="primary"
                htmlType="submit"
                loading={
                  createSite.isPending || updateSite.isPending || globalLoading
                }
              >
                {isLastStep ? "Complete" : "Next"}
              </Button>
            </DisableForViewer>
          </div>
        </Form>
      </div>

      {globalError && (
        <Alert
          message="Error"
          description={globalError}
          type="error"
          showIcon
          className="mt-4"
        />
      )}
    </div>
  );
};

export default SiteStepper;
